// src/components/foam/FoamAlreadyClaimed.js
//
// Screen 7 · Already claimed / Transfer
// Doc: handoff README "Screen 7" + SPEC §2.7
//
// Phase 1: visually complete. The "Request transfer" button is wired in Phase 3
// when the requestOwnershipTransfer Cloud Function is built.

// Updated 2026-06-04: WAKA Design System pass — Saira headline, pinned-footer
// layout, square accent CTA, carbon info block.

import React from 'react';
import { FScreen, FPage, FHeader, FLabel, FHeadline, FBtn } from './FoamPrimitives';

const FoamAlreadyClaimed = ({ publicRegistration, onRequestTransfer, onCancel }) => {
  if (!publicRegistration) {
    return (
      <FPage>
        <FHeader />
        <p className="text-[16px] text-foam-ink-2 mt-[40px] text-center">
          Nothing to show.
        </p>
      </FPage>
    );
  }

  const {
    registeredUserName,
    sku,
    verified,
    warrantyExpiryDate,
    registrationDate,
  } = publicRegistration;

  const hasName = !!registeredUserName;
  const firstName = (registeredUserName || '').split(' ')[0] || 'the owner';
  const lastInitial = (registeredUserName || '').split(' ')[1]?.charAt(0) || '';
  const ownerLabel = lastInitial ? `${firstName} ${lastInitial}.` : firstName;
  // Firestore Timestamps need .toDate() — new Date(timestampObj) renders
  // "Invalid Date" (Nigel, 2026-06-05 prod sighting).
  const regDateObj = registrationDate
    ? (registrationDate.toDate ? registrationDate.toDate() : new Date(registrationDate))
    : null;
  const registeredMonth = regDateObj && !isNaN(regDateObj.getTime())
    ? regDateObj.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';
  const warrantyMonthsLeft = warrantyExpiryDate
    ? Math.max(0, Math.floor((new Date(warrantyExpiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30)))
    : null;

  const header = (
    <FHeader right={
      <button
        type="button"
        onClick={onCancel}
        className="font-sans text-[14px] text-foam-ink-2"
      >
        Cancel
      </button>
    } />
  );

  const footer = (
    <>
      <FBtn primary onClick={onRequestTransfer}>
        Request transfer →
      </FBtn>
      <div className="text-[13px] text-foam-ink-2 text-center leading-[1.5]">
        Not yours?{' '}
        <a
          href="mailto:hello@waka.surf?subject=Report%20serial"
          className="underline text-foam-ink font-semibold"
        >
          Report this serial
        </a>
      </div>
    </>
  );

  return (
    <FScreen header={header} footer={footer}>
      <FLabel color="var(--foam-orange)">● Already claimed</FLabel>

      <FHeadline size={50} lineHeight={0.92} className="mt-[2px]">
        This board<br />
        already has an owner<FHeadline.Accent>.</FHeadline.Accent>
      </FHeadline>

      <p className="text-[15px] leading-[1.5] text-foam-ink-2">
        {hasName ? (
          <>Registered to <strong className="text-foam-ink">{ownerLabel}</strong>{registeredMonth ? <> in {registeredMonth}</> : ''}.</>
        ) : (
          <>This board was registered{registeredMonth ? <> in {registeredMonth}</> : ''}.</>
        )}{' '}
        If you bought it from {hasName ? firstName : 'the previous owner'},
        request a transfer — they confirm with one tap.
      </p>

      <div
        className="flex flex-col"
        style={{ border: '1.5px solid var(--foam-line)' }}
      >
        {[
          { label: 'SKU',       value: sku },
          { label: 'Status',    value: verified ? '● Verified' : '○ Pending' },
          { label: 'Warranty',  value: warrantyMonthsLeft != null ? `${warrantyMonthsLeft} months left` : '—' },
        ].map((row, i, arr) => (
          <div
            key={row.label}
            className="flex justify-between items-center px-[16px] py-[14px]"
            style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--foam-hair)' : 'none' }}
          >
            <div className="text-[14px] text-foam-ink-2">{row.label}</div>
            <div className="font-mono text-[13px] text-foam-ink">{row.value}</div>
          </div>
        ))}
      </div>

      <div
        className="p-[18px] flex flex-col gap-[10px]"
        style={{ background: 'var(--foam-carbon)', color: 'var(--foam-paper)' }}
      >
        <div
          className="font-mono text-[12px] uppercase font-medium"
          style={{ color: 'var(--foam-orange)', letterSpacing: '0.14em' }}
        >
          How transfer works
        </div>
        <p className="text-[14px] leading-[1.5]" style={{ color: '#D7D4C8' }}>
          We email {firstName} a one-tap <strong style={{ color: 'var(--foam-paper)' }}>"yes, I sold this"</strong> link.
          Once they confirm, the board moves to your account and your warranty
          picks up the remaining time. WAKA gets notified — we're not in the
          middle, just keeping the records honest.
        </p>
      </div>
    </FScreen>
  );
};

export default FoamAlreadyClaimed;
